import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import { useParams } from "react-router-dom";
import { activityApi } from "../../api/activity";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PAGE_SIZE = 40;
const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Comercial",
  auth: "Accesos",
  user: "Usuarios",
  project: "Proyecto",
  landing: "Página",
  product: "Productos / servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "ERP",
  cash: "Caja",
  stock: "Stock",
  billing: "Billing",
  push: "Notificaciones",
  integration: "Integraciones",
  system: "Sistema",
};

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "medium" }).format(date);
}

function actorLabel(event) {
  const actor = event?.actor || {};
  return actor.name || actor.email || (actor.type === "visitor" ? "Visitante web" : actor.type === "customer" ? "Cliente registrado" : actor.type === "webhook" ? "Webhook" : "Sistema");
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>{icon}</Box>
          <Box>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>{title}</Typography>
            <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 950 }}>{value}</Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.50)" }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectActivityPage() {
  const { projectId } = useParams();
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [journeys, setJourneys] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = React.useState({ category: "", source: "", q: "" });
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { page, pageSize: PAGE_SIZE, ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) };
      const [activityResponse, summaryResponse, journeysResponse] = await Promise.all([
        activityApi.project(projectId, params),
        activityApi.projectSummary(projectId, 24),
        activityApi.projectJourneys(projectId, { hours: 168, limit: 20 }),
      ]);
      setItems(activityResponse.items || []);
      setPagination(activityResponse.pagination || { page, pages: 1, total: 0 });
      setSummary(summaryResponse.summary || null);
      setJourneys(journeysResponse.items || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar la actividad del proyecto");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, filters]);

  React.useEffect(() => { load(); }, [load]);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack spacing={3}>
      <EditorialIntro
        title="Actividad y recorridos"
        titleVariant="h3"
        context="Visitas y operaciones del proyecto en un solo historial."
        helpTitle="Qué incluye este historial"
        helpText="Reúne visitas a la web, consultas, carrito, pedidos y operaciones realizadas por dueños o empleados para reconstruir el recorrido completo."
        action={<Button onClick={load} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}><Metric icon={<HistoryRoundedIcon />} title="Eventos en 24 h" value={summary?.total ?? "—"} helper="Web y operación" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Metric icon={<PeopleAltRoundedIcon />} title="Visitantes únicos" value={summary?.uniqueVisitors ?? "—"} helper="Navegadores anónimos" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Metric icon={<BoltRoundedIcon />} title="Alta intención" value={summary?.highIntent ?? "—"} helper="WhatsApp, carrito y checkout" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Metric icon={<DashboardRoundedIcon />} title="Usuarios del panel" value={summary?.uniquePanelUsers ?? "—"} helper="Dueños y empleados activos" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <RouteRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Recorridos recientes</Typography>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>Secuencia real de páginas y acciones dentro de cada sesión.</Typography>
          </Box>
        </Stack>
        <Stack spacing={1.5}>
          {journeys.slice(0, 10).map((journey) => (
            <Box key={`${journey.channel}-${journey.sessionId}`} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={journey.channel === "storefront" ? <StorefrontRoundedIcon /> : <DashboardRoundedIcon />} label={journey.channel === "storefront" ? "Web pública" : "Panel"} />
                    <Chip size="small" variant="outlined" label={`${journey.stepsCount || 0} pasos`} />
                    {journey.highIntent ? <Chip size="small" color="success" label={`${journey.highIntent} acciones comerciales`} /> : null}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1, color: "rgba(25,36,47,.66)" }}>
                    {(journey.steps || []).slice(-8).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", flexShrink: 0 }}>{dateTime(journey.lastAt)}</Typography>
              </Stack>
            </Box>
          ))}
          {!loading && !journeys.length && <Alert severity="info">Todavía no hay sesiones con recorrido registrado.</Alert>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small"><InputLabel>Categoría</InputLabel><Select label="Categoría" value={filters.category} onChange={(event) => updateFilter("category", event.target.value)}><MenuItem value="">Todas</MenuItem>{Object.entries(CATEGORY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</Select></FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small"><InputLabel>Origen</InputLabel><Select label="Origen" value={filters.source} onChange={(event) => updateFilter("source", event.target.value)}><MenuItem value="">Todos</MenuItem><MenuItem value="storefront">Web pública</MenuItem><MenuItem value="panel">Panel</MenuItem><MenuItem value="webhook">Webhook</MenuItem><MenuItem value="api">API / sistema</MenuItem></Select></FormControl>
          </Grid>
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Buscar actividad, persona o producto" value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} /></Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box><Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Historial completo</Typography><Typography variant="body2" sx={{ color: "rgba(25,36,47,.56)" }}>{pagination.total || 0} eventos</Typography></Box>
          {loading && <CircularProgress size={24} />}
        </Stack>
        <Stack spacing={1.25}>
          {items.map((event) => (
            <Box key={event.id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,.05)" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{event.title}</Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", mt: .25 }}>{event.description}</Typography>
                  <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}><Chip size="small" label={CATEGORY_LABELS[event.category] || event.category} /><Chip size="small" variant="outlined" label={event.source === "storefront" ? "Web pública" : event.source === "panel" ? "Panel" : event.source} /><Chip size="small" variant="outlined" label={actorLabel(event)} /></Stack>
                </Box>
                <Box sx={{ flexShrink: 0, textAlign: { md: "right" } }}><Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)", fontWeight: 800 }}>{dateTime(event.occurredAt)}</Typography><Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.4)" }}>{event.action}</Typography></Box>
              </Stack>
            </Box>
          ))}
          {!loading && !items.length && <Alert severity="info">No hay actividad para estos filtros.</Alert>}
        </Stack>
        {pagination.pages > 1 && <Stack alignItems="center" sx={{ mt: 3 }}><Pagination page={pagination.page || page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" /></Stack>}
      </Paper>
    </Stack>
  );
}
